import type { AppointmentMapper, UserMapper } from "@phurshell/psiapp-frontend-core/domain";
import { toAppointmentView } from "@/app/formatters/appointment";
import { toPsiView } from "@/app/formatters/psi";

type HomeInfoDataSource = { getUserHomeInfo: () => Promise<any> };

const imgUrl = (path?: string | null) => (path ? `${process.env.VUE_APP_BASE_IMG_URL}${path}` : "");

/**
 * Sessão em aberto (in_progress), com o mesmo título/mensagem/imagem do getPatientHomeInfo do core
 * (o app RN usa esse formato no card "Sessão em aberto").
 */
export function toOngoingAppointment(p: any) {
  if (!p) return null;
  const inProgress = p.status === "em andamento";
  return {
    id: p.id,
    status: p.status,
    title: p.type === "imediate" ? "Sessão imediata" : "Sessão agendada",
    msg: inProgress ? "Sessão em andamento" : p.type === "imediate" && p.status === "pendente" ? "Buscando sessão com psi" : "",
    img: inProgress ? imgUrl(p.psychologist?.profile_image) : null,
  };
}

/** Vídeos do "Como funciona o Psiapp" (mesmos campos do core: name = description). */
export const toHomeVideo = (v: any) => ({
  ...v,
  name: v.description,
  image: imgUrl(v.thumbnail_path),
  video: imgUrl(v.video_path),
});

/**
 * Dados da Home do paciente: sessões agendadas e "meus psis".
 * Usa o datasource do core (resposta crua) e não o `userRepo.getPatientHomeInfo`,
 * porque este reduz cada sessão a {id, psychologist, status, date} e troca o status
 * pelo do reagendamento, e os cards precisam do reagendamento completo.
 */
export class GetHomeInfo {
  constructor(
    private readonly dataSource: HomeInfoDataSource,
    private readonly appointmentMapper: AppointmentMapper<any, any, any>,
    private readonly userMapper: UserMapper<any, any, any>,
  ) {}

  async execute() {
    const res = await this.dataSource.getUserHomeInfo();
    return {
      ongoing: toOngoingAppointment(res.in_progress),
      unread_notifications: Number(res.unread_notifications) || 0,
      videos: (res.videos ?? []).map(toHomeVideo),
      scheduled_appointments: (res.agendado ?? []).map((a: any) =>
        toAppointmentView(this.appointmentMapper.toDomain(a)),
      ),
      my_psis: (res.recent_psy ?? [])
        .map((r: any) => r.psychologist)
        .filter((p: any) => p && !p.deleted_at)
        .map((p: any) => toPsiView(this.userMapper.toDomain(p))),
    };
  }
}
